<template>
  <el-select
    ref="selectRef"
    :automatic-dropdown="props.automaticdropdown"
    :size="sizeMap[props.size]"
    :class="[
      style.select,
      border ? '' : style.noborder,
      `select-${props.size}`,
      props.filled ? style.filled : '',
    ]"
    :popper-class="`${style.popper} select-popper-${props.size}`"
    :clear-icon="CircleCloseFilled"
    :suffix-icon="CaretBottom"
    @input="onInput"
    @visible-change="visibleChange"
  >
    <template v-if="slots.default" #default>
      <slot />
    </template>
    <template v-else #default>
      <el-option
        v-for="item in data"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
    </template>
    <template #empty>
      <slot v-if="slots.empty" name="empty" />
      <template v-else>
        <div class="text-center pt48 pb48">
          <SvgIcon class="w64 h64" icon="Illustration-64-Light-No_Orders" />
          <p class="text-text1 fz12 weight-500 mt4">
            {{ $t("无搜索结果") }}
          </p>
        </div>
      </template>
    </template>
  </el-select>
</template>

<script setup lang="ts">
import { ref, reactive, computed, useSlots, nextTick } from 'vue';
import CaretBottom from './CaretBottom.vue';
import CircleCloseFilled from './CircleCloseFilled.vue';

const emit = defineEmits(['visible-change']);
const slots = useSlots();
const props = defineProps({
  size: {
    validator(value: string) {
      return ['max', 'large', 'default', 'small'].indexOf(value) > -1;
    },
    type: String,
    default: 'default'
  },
  border: {
    type: Boolean,
    default: true
  },
  options: {
    type: Array,
    default: () => []
  },
  filled: {
    type: Boolean,
    default: false
  },
  automaticdropdown: {
    type: Boolean,
    default: false
  }
});

const selectRef = ref<any>(null);

const focus = () => {
  nextTick(() => {
    console.log(selectRef.value);
    selectRef.value?.focus();
    nextTick(() => {
      selectRef.value?.$el.click();
      // Ensure the select is focused before clicking
      // This is useful for cases where the select needs to be opened programmatically
    });
  });
};

defineExpose({
  focus
});

const sizeMap: any = reactive({
  max: 'large',
  large: 'large',
  default: 'default',
  small: 'small',
  mini: 'small'
});

const keyword = ref('');

const data = computed(() => {
  if (keyword.value) {
    return props.options.filter((item: any) =>
      item.label.toLowerCase().includes(keyword.value.toLowerCase())
    );
  }
  return props.options;
});

const onInput = (event: any) => {
  keyword.value = event.target.value;
};

const visibleChange = (visible: boolean) => {
  emit('visible-change', visible);
  keyword.value = '';
};
</script>

<style lang="scss" module="style">
.select {
  --el-text-color-regular: var(--text-text1);
  --el-fill-color-blank: var(--base-base1);
  --el-border-color: var(--border-border1);
  &:hover {
    --el-select-border-color-hover: var(--gray-gray10);
  }

  :global(.el-input__wrapper) {
    --el-select-input-focus-border-color: var(--gray-gray10);
    --el-input-border-color: var(--border-border1);
  }

  :global(.el-input__inner) {
    font-size: var(--font-size);
    font-weight: var(--font-weight);
  }

  :global(.el-input__inner::placeholder) {
    color: var(--text-text4);
  }

  &:global {
    &:not(.select-clear-hover) .el-input .el-select__caret.el-icon {
      //background:url(./arw.svg) center no-repeat !important;
      background: none !important;
    }
    &.el-select:not(.select-clear-hover)
      .el-input
      .el-select__caret.el-icon
      svg {
      display: block;
      color: var(--text-text1);
      font-size: var(--font-size);
      font-weight: var(--font-weight);
    }
  }
}

.filled {
  :global(.el-input__wrapper) {
    --el-input-bg-color: var(--fill-fill4);
    --el-input-border-color: var(--fill-fill4);
  }
}
.noborder {
  :global(.el-input__wrapper) {
    --el-select-input-focus-border-color: none;
    box-shadow: none !important;
  }
}

:global {
  .select {
    &-max {
      --el-border-radius: var(--radius-max);
      height: var(--height-max);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-max);
        --font-size: var(--font-size-max);
        --font-weight: var(--font-weight-max);
      }
    }

    &-large {
      --el-border-radius: var(--radius-large);
      height: var(--height-large);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-large);
        --font-size: var(--font-size-large);
        --font-weight: var(--font-weight-large);
      }
    }

    &-default {
      --el-border-radius: var(--radius-default);
      height: var(--height-default);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-default);
        --font-size: var(--font-size-default);
        --font-weight: var(--font-weight-default);
      }
    }

    &-small {
      --el-border-radius: var(--radius-small);
      height: var(--height-small);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-small);
        --font-size: var(--font-size-small);
        --font-weight: var(--font-weight-small);
      }
    }
    &-mini {
      --el-border-radius: var(--radius-mini);
      height: var(--height-mini);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-mini);
        --font-size: var(--font-size-mini);
        --font-weight: var(--font-weight-mini);
      }
    }
  }
  .select-popper {
    &-max {
      --el-popper-border-radius: var(--radius-max);
    }

    &-large {
      --el-popper-border-radius: var(--radius-large);
    }

    &-default {
      --el-popper-border-radius: var(--radius-default);
    }

    &-small {
      --el-popper-border-radius: var(--radius-small);
    }
    &-small {
      --el-popper-border-radius: var(--radius-mini);
    }
  }
}

.popper {
  &:global(.el-select__popper) {
    --el-bg-color-overlay: var(--fill-fill8);
    margin-top: -10px !important;
    border-color: var(--border-border1);
    overflow: hidden;
  }
  :global {
    .el-select-dropdown__item {
      color: var(--text-text2);
    }
    .el-select-dropdown__item.selected {
      color: var(--text-text1) !important;
    }
  }
}
</style>
